import { useContext, useState } from "react";
import { motion } from "framer-motion";
import { LanguageContext } from "../../../context/LanguageContext";

export default function Pricing() {
  const { t, isRTL } = useContext(LanguageContext);
  const plans = t("pricing.plans", { returnObjects: true }) || [];

  const [selectedAddons, setSelectedAddons] = useState({});
  const [userQuantities, setUserQuantities] = useState({});

  const calculateTotal = (plan, index) => {
    let total = plan.price;
    const addons = selectedAddons[index] || [];

    addons.forEach((addon) => {
      if (addon.type === "perUser") {
        const qty = userQuantities[index] || 1;
        total += addon.price * qty;
      } else {
        total += addon.price;
      }
    });

    return total;
  };

  return (
    <section dir={isRTL ? "rtl" : "ltr"} className="bg-white py-24">
      <div className="mx-auto max-w-7xl px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <h2 className="text-4xl font-extrabold text-black">
            {t("pricing.title")}
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            {t("pricing.subtitle")}
          </p>
        </motion.div>

        {/* Plans */}
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.03 }}
              className={`rounded-2xl border p-8 shadow-sm ${
                plan.highlighted
                  ? "border-indigo-600 bg-indigo-50"
                  : "border-gray-200 bg-rose-50"
              }`}
            >
              <h3 className="text-xl font-bold text-black">{plan.name}</h3>
              <p className="mt-2 text-sm text-gray-600">{plan.description}</p>

              {/* Dynamic Price */}
              <div className="mt-6 flex items-end gap-1">
                <span className="text-4xl font-extrabold text-black">
                  ${calculateTotal(plan, index).toFixed(2)}
                </span>
                <span className="text-sm text-gray-500">
                  {plan.period || "/mo"}
                </span>
              </div>

              {/* Features */}
              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, i) => (
                  <li key={i} className="text-sm text-gray-700">
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Addons */}
              {plan.addons && (
                <div className="mt-6 space-y-4 border-t pt-4">
                  <p className="text-sm font-semibold text-gray-800">
                    Optional Add-ons
                  </p>

                  {plan.addons.map((addon, i) => {
                    const isChecked =
                      selectedAddons[index]?.some(
                        (a) => a.label === addon.label
                      ) || false;

                    return (
                      <div key={i} className="space-y-2">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              setSelectedAddons((prev) => {
                                const current = prev[index] || [];

                                if (e.target.checked) {
                                  return {
                                    ...prev,
                                    [index]: [...current, addon],
                                  };
                                } else {
                                  return {
                                    ...prev,
                                    [index]: current.filter(
                                      (a) => a.label !== addon.label
                                    ),
                                  };
                                }
                              });
                            }}
                          />
                          {addon.label} (+${addon.price}
                          {addon.type === "perUser" ? "/user" : ""}/mo)
                        </label>

                        {/* Quantity input for perUser */}
                        {addon.type === "perUser" && isChecked && (
                          <div className="ml-6 flex items-center gap-3">
                            <span className="text-sm text-gray-600">
                              Number of users:
                            </span>

                            <input
                              type="number"
                              min="1"
                              value={userQuantities[index] || 1}
                              onChange={(e) =>
                                setUserQuantities({
                                  ...userQuantities,
                                  [index]:
                                    parseInt(e.target.value) || 1,
                                })
                              }
                              className="w-20 rounded-md border px-2 py-1 text-sm"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                className={`mt-8 w-full rounded-lg py-3 font-semibold transition ${
                  plan.highlighted
                    ? "bg-indigo-600 text-white hover:bg-indigo-700"
                    : "border border-gray-300 bg-white hover:bg-gray-50"
                }`}
              >
                {plan.cta}
              </button>
            </motion.div>
          ))}
        </div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="mt-12 text-center text-sm text-gray-500"
        >
          {t("pricing.contactText")}{" "}
          <a
            href="mailto:sales@teqa.app"
            className="text-indigo-600 hover:underline"
          >
            sales@teqa.app
          </a>
        </motion.p>
      </div>
    </section>
  );
}
